import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, KV, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { adminApi, downloadJson } from '../lib/adminApi';
import { fmtDate, fmtDateTime } from '../lib/format';

export const DeletionDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmManual, setConfirmManual] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const { data: rows, error, isLoading } = useSel<any[]>(['deletion', id], () =>
    supabase.from('deletion_requests').select('*').eq('id', id).limit(1));
  const d = rows?.[0];

  const { data: footprint } = useRpc<{ item: string; n: number }[]>(
    'admin_user_footprint', { p_user: d?.user_id }, { enabled: !!d?.user_id },
  );
  const { data: subRows } = useSel<any[]>(['deletion-sub', d?.user_id], () =>
    supabase.from('admin_sub_classes').select('*').eq('user_id', d!.user_id).limit(1),
    { enabled: !!d?.user_id });
  const sub = subRows?.[0];
  const stripeBlocked = !!sub?.stripe_subscription_id && ['active', 'trialing', 'past_due'].includes(sub?.status);

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} />;
  if (!d) return <ErrorPanel error="Deletion request not found" />;

  const age = Math.floor((Date.now() - new Date(d.requested_at).getTime()) / 86_400_000);

  const doExport = async () => {
    if (!d.user_id) return;
    setExporting(true);
    try {
      const res = await adminApi<{ export: unknown }>('export_user', { userId: d.user_id });
      downloadJson(res.export, `intervyo-deletion-export-${String(d.user_id).slice(0, 8)}.json`);
    } finally { setExporting(false); }
  };

  return (
    <div>
      <Link to="/compliance" className="tap inline-flex items-center gap-1 text-[13px] lg:text-[12px] text-stone-500 lg:text-stone-400 hover:text-stone-700 mb-1 lg:mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Compliance
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-stone-900">Deletion request from {d.user_email_at_request ?? 'unknown'}</h1>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[12px] text-stone-500">
            <Badge tone={d.status === 'completed' ? 'emerald' : 'rose'}>{d.status}</Badge>
            <span>scope {d.scope}</span>
            <span>· requested {fmtDate(d.requested_at)}</span>
            {d.status === 'pending' && <Badge tone={age > 21 ? 'rose' : age > 14 ? 'amber' : 'stone'}>Day {age} of the one-month window</Badge>}
            {d.user_id && <span>· <UserLink id={d.user_id} /></span>}
          </div>
        </div>
      </div>

      {d.status === 'completed' ? (
        <Section title="Completed">
          <dl>
            <KV k="Completed at">{fmtDateTime(d.completed_at)}</KV>
            <KV k="Confirmation email">{d.confirmation_email_sent_at ? fmtDateTime(d.confirmation_email_sent_at) : 'never sent'}</KV>
            <KV k="Notes">{d.notes ?? 'none'}</KV>
          </dl>
        </Section>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          <Section title="Data footprint" sub="Every table and storage prefix holding this person’s data right now.">
            {!d.user_id ? (
              <EmptyNote>The auth user is already gone; only this request row survives.</EmptyNote>
            ) : (footprint ?? []).length === 0 ? <LoadingRow /> : (
              <TableWrap phone="table">
                <thead><tr><Th>Where</Th><Th>Rows</Th></tr></thead>
                <tbody>
                  {(footprint ?? []).map(f => (
                    <tr key={f.item}><Td>{f.item}</Td><Td className="tabular-nums">{f.n}</Td></tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Section>

          <Section title="Fulfilment" sub="In order. Every step is audited; the confirmation email sends automatically at the end.">
            <ol className="space-y-3 text-[13px] text-stone-700">
              <li className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-x-3 gap-y-2">
                <span><span className="font-semibold">1. Export the data bundle</span><br />
                  <span className="text-[12px] text-stone-500">Keep a copy in case the requester asks what was held.</span></span>
                <Button size="sm" busy={exporting} onClick={doExport} disabled={!d.user_id}>Export</Button>
              </li>
              <li className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-x-3 gap-y-2">
                <span><span className="font-semibold">2. Stripe check</span><br />
                  <span className="text-[12px] text-stone-500">
                    {stripeBlocked
                      ? 'A live Stripe subscription exists. Cancel it in Stripe first; the fulfilment refuses to run until then.'
                      : 'No live Stripe subscription. Nothing to cancel.'}
                  </span></span>
                {sub?.stripe_customer_id && (
                  <a className="tap text-indigo-600 text-[12px] font-semibold inline-flex items-center gap-1 shrink-0" target="_blank" rel="noreferrer"
                     href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`}>
                    Open Stripe <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </li>
              <li className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-x-3 gap-y-2">
                <span><span className="font-semibold">3. Delete everything</span><br />
                  <span className="text-[12px] text-stone-500">Storage sweep (recordings and CVs), then the auth user; the database cascades. Marks this request completed and emails confirmation to {d.user_email_at_request}.</span></span>
                <Button size="sm" variant="danger" disabled={stripeBlocked} onClick={() => setConfirmDelete(true)}>Delete everything</Button>
              </li>
            </ol>
            {!d.user_id && (
              <div className="mt-3 pt-3 border-t border-stone-100">
                <p className="text-[12px] text-stone-500 mb-2">The account itself is already deleted. Mark this request completed to close it.</p>
                <Button size="sm" onClick={() => setConfirmManual(true)}>Mark completed</Button>
              </div>
            )}
            {result && (
              <pre className="mt-3 text-[11px] bg-stone-50 rounded-md p-2 overflow-x-auto text-stone-600">{JSON.stringify(result, null, 2)}</pre>
            )}
          </Section>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete} onClose={() => setConfirmDelete(false)}
        title={`Permanently delete ${d.user_email_at_request ?? 'this user'}`}
        tone="danger" confirmLabel="Delete permanently"
        body={<>This is irreversible. Storage files are removed, the auth account is deleted, every user-scoped row cascades, this request is marked completed, and a confirmation email goes to the requester. Export first if you have not.</>}
        onConfirm={async () => {
          const res = await adminApi('fulfil_deletion', { requestId: id });
          setResult(res);
          queryClient.invalidateQueries();
        }}
      />
      <ConfirmDialog
        open={confirmManual} onClose={() => setConfirmManual(false)}
        title="Mark this request completed" confirmLabel="Mark completed"
        body={<>Records completion with your account and the timestamp. Use when the deletion happened outside this workflow.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_complete_deletion', { p_request_id: id });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries();
        }}
      />
    </div>
  );
};
